import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, UploadSimple, Trash } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Coach-only modal to upload/replace/remove a custom thumbnail for a milestone
 * badge. The thumbnail is stored once per coach (user_id) and reused across
 * every client that has unlocked the same badge.
 */
export default function MilestoneThumbnailModal({ badge, currentPath, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    // Fetch the existing thumbnail (if any) as a blob URL for the preview.
    let revokeUrl = null;
    let mounted = true;
    if (currentPath) {
      axios.get(`${API}/files/${currentPath}`, { responseType: "blob" })
        .then((r) => {
          if (!mounted) return;
          revokeUrl = URL.createObjectURL(r.data);
          setPreview(revokeUrl);
        })
        .catch((err) => console.debug("[MilestoneThumbnail]", err));
    } else {
      setPreview(null);
    }
    return () => {
      mounted = false;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [currentPath]);

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/milestones/${encodeURIComponent(badge.code)}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Miniatura aggiornata");
      onChanged?.();
      onClose?.();
    } catch (err) {
      console.error("[MilestoneThumbnail] upload failed", err);
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!currentPath) return;
    if (!window.confirm("Rimuovere la miniatura personalizzata?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/milestones/${encodeURIComponent(badge.code)}/media`);
      toast.success("Miniatura rimossa");
      onChanged?.();
      onClose?.();
    } catch (err) {
      console.error("[MilestoneThumbnail] delete failed", err);
      toast.error("Errore nella rimozione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
      data-testid="milestone-thumb-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-[#0F0F0F] border border-[#262626] rounded-t-2xl sm:rounded-2xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold">
              Miniatura milestone
            </p>
            <h3
              className="text-lg font-bold text-white tracking-tight mt-1"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {badge.name.toUpperCase()}
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">{badge.description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-[#262626] text-zinc-400 hover:text-white flex items-center justify-center"
            aria-label="Chiudi"
            data-testid="milestone-thumb-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="aspect-video rounded-xl bg-[#0A0A0A] border border-[#1A1A1A] overflow-hidden flex items-center justify-center">
          {preview ? (
            <img src={preview} alt={`Anteprima ${badge.name}`} className="w-full h-full object-cover" />
          ) : (
            <p className="text-zinc-600 text-xs italic">Nessuna foto caricata</p>
          )}
        </div>

        <p className="text-[11px] text-zinc-500 leading-relaxed">
          La foto sarà usata come miniatura per <span className="text-zinc-300 font-medium">tutti i clienti</span> che
          sbloccano questa milestone. Formati supportati: JPG, PNG, GIF, WEBP.
        </p>

        <div className="flex gap-2">
          <label
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-medium cursor-pointer transition-colors ${
              busy ? "opacity-50 pointer-events-none" : ""
            }`}
            data-testid="milestone-thumb-upload"
          >
            <UploadSimple size={16} weight="bold" />
            {currentPath ? "Sostituisci" : "Carica foto"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePick}
              disabled={busy}
            />
          </label>
          {currentPath && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={busy}
              className="px-3 py-2.5 rounded-lg bg-[#1A1A1A] border border-[#262626] text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:border-[#FF3B30]/40 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="milestone-thumb-remove"
            >
              <Trash size={14} weight="bold" /> Rimuovi
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
